<template>
  <iBaseModal
    class="i-resume-builder-modal-1"
    use-light-close-icon
    :visible="props.visible"
    :fully-customisable="true"
    max-width="960px"
    @click-on-close="emit('close')"
  >
    <img
      class="exclusive"
      :src="ImageExclusive"
    />
    <div class="inner">
      <div class="top">
        <div>The Ultimate Job-Ready Resume Package</div>
        <div class="sub"><img :src="IconCoupon" />You’ve Unlocked $250 in FREE ADD-ONS</div>
      </div>
      <div class="middle">
        <template
          v-for="(data, i) in details"
          :key="i"
        >
          <div class="row">
            <div class="number">{{ i + 1 }}</div>
            <img
              class="image"
              :src="data.image"
            />
            <div class="rest">
              <div class="title">{{ data.title }}</div>
              <div class="perks">
                <div
                  v-for="(perk, a) in data.perks"
                  :key="a"
                  class="perk"
                >
                  <img
                    class="check"
                    :src="IconCheck"
                  />
                  <div>{{ perk }}</div>
                </div>
              </div>
            </div>
            <div class="price">
              <span class="free">FREE</span>
              <div class="badge">SAVE <span class="dollar">$</span>{{ data.price }}</div>
            </div>
          </div>
        </template>
      </div>
      <div class="bottom">
        <div>Limited Time Only</div>
        <div
          class="action"
          @click="emit('claim-resume-builder')"
        >
          Claim Free Gift <img :src="IconArrow" />
        </div>
        <div class="offer">
          Offer ends in:
          <span class="timer"
            >{{ props.minutesLeft }}:{{
              parseInt(props.secondsLeft) < 10 ? "0" + props.secondsLeft : props.secondsLeft
            }}</span
          >
        </div>
      </div>
    </div>
  </iBaseModal>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, watch } from "vue"
import { breakpointMd } from "@/styles/variables"

import useIsScreenSize from "@/composables/useIsScreenSize"

import iBaseModal from "@/components/base/BaseModal.vue"

// ASSETS
import IconArrow from "@/assets/icons/icon-arrow-right-white.svg"
import IconCoupon from "@/assets/icons/icon-coupon-white.svg"
import IconCheck from "@/assets/icons/icon-check-green.svg"
import ImageExclusive from "@/assets/welcome/rb-exclusive-deal1.svg"
import ImageAI from "@/assets/welcome/rb-ai-resume1.svg"
import ImageLifetime from "@/assets/welcome/rb-lifetime-access1.svg"
import ImageMatch from "@/assets/welcome/rb-job-match1.svg"

// EMITS
const emit = defineEmits(["close", "claim-resume-builder"])

// PROPS
const props = defineProps({
  visible: { type: Boolean, required: true },
  minutesLeft: { type: String, required: true },
  secondsLeft: { type: String, required: true }
})

// COMPOSABLES
const isMdOrLarger = useIsScreenSize(breakpointMd)

// COMPUTED
const details = computed(() => [
  {
    image: ImageAI,
    title: "Instant AI Resume Builder",
    perks: [
      "Create your resume in minutes",
      "AI writing, Powered by ChatGPT-5",
      "Sound instantly more professional",
      ...(isMdOrLarger.value ? ["Get 95% Job Match Rate"] : [])
    ],
    price: 50
  },
  {
    image: ImageLifetime,
    title: "Lifetime Access",
    perks: [
      "Iterate without limits",
      "Unlimited versions & downloads",
      "Edit anytime. Keep it forever",
      "No renewals or Subscription"
    ],
    price: 185
  },
  {
    image: ImageMatch,
    title: "Job Match Optimizer",
    perks: [
      "Tailor your resume to each job",
      "Keyword and ATS alignment",
      "Role-specific optimization",
      "Optimize for ATS screening"
    ],
    price: 15
  }
])

// FUNCTIONS
function lockBodyScroll() {
  document.body.classList.add("resume-builder-modal-no-scroll")
}

function unlockBodyScroll() {
  document.body.classList.remove("resume-builder-modal-no-scroll")
}

// LIFECYCLE HOOKS
onBeforeUnmount(() => {
  unlockBodyScroll()
})

// WATCHERS
watch(
  () => props.visible,
  (open) => {
    if (open) lockBodyScroll()
    else unlockBodyScroll()
  },
  { immediate: true }
)
</script>

<style scoped lang="scss">
.i-resume-builder-modal-1 {
  overscroll-behavior: contain;

  .exclusive {
    position: absolute;
    top: -18px;
    left: 50%;
    width: 145px;
    transform: translateX(-50%);
  }

  .inner {
    @include flex(column, start, center, $gap: 16px);

    margin: 30px 20px 20px;

    @media screen and (min-width: $breakpoint-sm) {
      gap: 18px;
    }

    @media screen and (min-width: $breakpoint-md) {
      @include flex(column, start, stretch, $gap: 20px);

      margin: 45px 35px 35px;
    }

    .top {
      @include flex(column, start, center, $gap: 8px);
      @include text-xl;

      color: $white;
      font-weight: 700;
      text-align: center;

      @media screen and (min-width: $breakpoint-xs) {
        @include text-xxl;
      }

      @media screen and (min-width: $breakpoint-sm) {
        @include display-xs;
      }

      @media screen and (min-width: $breakpoint-md) {
        @include display-md;

        gap: 10px;
        font-weight: 600;
      }

      .sub {
        @include text-md;

        font-weight: 500;
        text-align: center;

        @media screen and (min-width: $breakpoint-xs) {
          @include text-lg;
        }

        @media screen and (min-width: $breakpoint-md) {
          @include text-xxl;
        }

        img {
          width: 25px;
          margin-right: 6px;

          @media screen and (min-width: $breakpoint-md) {
            width: 45px;
            margin-right: 10px;
          }
        }
      }
    }

    .middle {
      @include flex(column, start, stretch, $gap: 20px);

      @media screen and (min-width: $breakpoint-md) {
        width: 100%;
      }

      .row {
        @include flex(column, center, center, $gap: 10px);
        @include border-radius-lg;

        position: relative;
        background-color: $white;

        @media screen and (min-width: $breakpoint-xs) {
          gap: 15px;
        }

        @media screen and (min-width: $breakpoint-md) {
          @include flex(row, start, center, $gap: 20px);

          padding-right: 24px;
          width: 100%;
          flex: 0 0 163px;
          height: 163px;
        }

        @media screen and (min-width: $breakpoint-lg) {
          flex: 0 0 120px;
          height: 120px;
          gap: 24px;
        }

        .number {
          @include flex(row, center, center);
          @include border-radius-rounded;
          @include text-xl;

          left: 0;
          top: 0;
          transform: translateY(-20%);
          position: absolute;
          background-color: $amethyst200;
          width: 48px;
          height: 48px;
          font-weight: 700;
          fill: #ead2fb;
          filter: drop-shadow(0 4px 4px #00000040);

          @media screen and (min-width: $breakpoint-md) {
            display: none;
          }
        }

        .image {
          width: 100%;
          border-top-left-radius: 24px;
          border-top-right-radius: 24px;

          @media screen and (min-width: $breakpoint-md) {
            max-width: 163px;
            flex: 0 0 163px;
            border-top-left-radius: 24px;
            border-bottom-left-radius: 24px;
            border-top-right-radius: 0;
          }

          @media screen and (min-width: $breakpoint-lg) {
            max-width: 120px;
            flex: 0 0 120px;
          }
        }

        .rest {
          @include flex(column, start, start, $gap: 8px);

          padding: 0 10px;
          width: 100%;

          @media screen and (min-width: $breakpoint-xs) {
            padding: 0 20px;
            gap: 10px;
          }

          @media screen and (min-width: $breakpoint-md) {
            padding: unset;
            margin-left: 28px;
            max-width: 255px;
            flex: 0 0 255px;
          }

          @media screen and (min-width: $breakpoint-lg) {
            @include flex(row, start, center, $gap: 10px);

            max-width: unset;
            flex: unset;
            margin-left: unset;
            gap: 24px;
          }

          .title {
            @include text-lg;

            font-weight: 700;

            @media screen and (min-width: $breakpoint-xs) {
              @include text-xl;
            }

            @media screen and (min-width: $breakpoint-lg) {
              max-width: 165px;
              flex: 0 0 165px;
              margin-left: 24px;
            }
          }

          .perks {
            @include flex(column, start, start, $gap: 1px);
            @include text-sm;

            @media screen and (min-width: $breakpoint-lg) {
              max-width: 255px;
              flex: 0 0 255px;
              margin-left: 24px;
            }

            .perk {
              @include flex(row, start, center, $gap: 5px);

              .check {
                width: 16px;

                @media screen and (min-width: $breakpoint-xs) {
                  width: 20px;
                }
              }
            }
          }
        }

        .price {
          @include flex(row, start, center, wrap, 0 10px);
          @include text-md;

          width: -webkit-fill-available;
          font-weight: 600;
          padding: 0 15px 15px;

          @media screen and (min-width: $breakpoint-xs) {
            padding: 0 20px 20px;
          }

          @media screen and (min-width: $breakpoint-md) {
            justify-content: center;
            padding: unset;
          }

          @media screen and (min-width: $breakpoint-lg) {
            justify-content: end;
          }

          .free {
            background: linear-gradient(90deg, #9810fa 0%, #e60076 100%);
            color: transparent;
            background-clip: text;
          }

          .badge {
            @include border-radius-xxl;

            background: linear-gradient(90deg, #9810fa 0%, #e60076 100%);
            color: $white;
            padding: 0 10px;

            @media screen and (min-width: $breakpoint-md) {
              padding: 3px 8px;
            }

            .dollar {
              @include text-xs;
            }
          }
        }
      }
    }

    .bottom {
      @include flex(row, center, center, wrap, 8px 14px);
      @include text-md;

      font-weight: 700;

      .action {
        @include flex(row, center, center, $gap: 12px);
        @include text-lg;
        @include border-radius-xxl;

        width: 100%;
        font-weight: 500;
        color: $white;
        background-image: linear-gradient(90deg, $bilbao600 0%, $bilbao400 100%);
        padding: 8px 24px;
        cursor: pointer;

        @media screen and (min-width: $breakpoint-md) {
          max-width: 300px;
        }

        &:hover {
          background-image: linear-gradient(90deg, $bilbao500 0%, $bilbao300 100%);
        }

        img {
          width: 20px;
        }
      }

      .offer {
        @include flex(row, center, center, $gap: 5px);

        font-weight: 400;

        .timer {
          @include text-xl;

          font-weight: 700;
          width: 59px;
        }
      }
    }
  }
}
</style>

<style lang="scss">
.i-resume-builder-modal-1 {
  &.i-base-modal {
    .outer-wrapper {
      @include border-radius-lg;

      background: linear-gradient(180deg, #fff0 0%, #fff 100%), linear-gradient(90deg, #9810fa 0%, #e60076 100%);

      @media screen and (min-width: $breakpoint-sm) {
        @include border-radius-xl;
      }
    }
  }
}
</style>
<style lang="scss">
body.resume-builder-modal-no-scroll {
  overflow: hidden;
}
</style>
